import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import type { LocaleType } from '../types/chat';

// Type for message modules
interface MessageModule {
  messages: Record<string, string>;
}

// Import translation resources directly with type assertion
import * as enMessagesModule from '../locales/en/messages.js';
import * as arMessagesModule from '../locales/ar/messages.js';
import * as zhMessagesModule from '../locales/zh/messages.js';
import * as jaMessagesModule from '../locales/ja/messages.js';
import * as ruMessagesModule from '../locales/ru/messages.js';
import * as deMessagesModule from '../locales/de/messages.js';
import * as frMessagesModule from '../locales/fr/messages.js';
import * as esMessagesModule from '../locales/es/messages.js';
import * as hiMessagesModule from '../locales/hi/messages.js';
import * as trMessagesModule from '../locales/tr/messages.js';
import * as svMessagesModule from '../locales/sv/messages.js';

// Cast imported modules to our message type
const enMessages = enMessagesModule as MessageModule;
const arMessages = arMessagesModule as MessageModule;
const zhMessages = zhMessagesModule as MessageModule;
const jaMessages = jaMessagesModule as MessageModule;
const ruMessages = ruMessagesModule as MessageModule;
const deMessages = deMessagesModule as MessageModule;
const frMessages = frMessagesModule as MessageModule;
const esMessages = esMessagesModule as MessageModule;
const hiMessages = hiMessagesModule as MessageModule;
const trMessages = trMessagesModule as MessageModule;
const svMessages = svMessagesModule as MessageModule;

/**
 * List of RTL (Right-to-Left) languages
 */
export const RTL_LOCALES = ['ar'];

/**
 * Supported locales and their display names
 */
export const SUPPORTED_LOCALES: Record<LocaleType, { english: string; native: string }> = {
  ar: { english: 'Arabic', native: 'العربية' },
  en: { english: 'English', native: 'English' },
  zh: { english: 'Chinese', native: '中文' },
  ja: { english: 'Japanese', native: '日本語' },
  ru: { english: 'Russian', native: 'Русский' },
  de: { english: 'German', native: 'Deutsch' },
  fr: { english: 'French', native: 'Français' },
  es: { english: 'Spanish', native: 'Español' },
  hi: { english: 'Hindi', native: 'हिन्दी' },
  tr: { english: 'Turkish', native: 'Türkçe' },
  sv: { english: 'Swedish', native: 'Svenska' }
};

// Initialize with resources for all supported languages
const resources = {
  en: { translation: enMessages.messages },
  ar: { translation: arMessages.messages },
  zh: { translation: zhMessages.messages },
  ja: { translation: jaMessages.messages },
  ru: { translation: ruMessages.messages },
  de: { translation: deMessages.messages },
  fr: { translation: frMessages.messages },
  es: { translation: esMessages.messages },
  hi: { translation: hiMessages.messages },
  tr: { translation: trMessages.messages },
  sv: { translation: svMessages.messages }
};

// Add default fallback translations for common UI elements
const defaultTranslations = {
  "Toggle theme": "Toggle theme",
  "Show message suggestions": "Show message suggestions",
  "Send message": "Send message"
};

// Ensure all languages have these basic translations
Object.keys(resources).forEach(lang => {
  const langResources = resources[lang as LocaleType].translation;
  for (const key in defaultTranslations) {
    if (!langResources[key]) {
      langResources[key] = defaultTranslations[key as keyof typeof defaultTranslations];
    }
  }
});

/**
 * Configure and initialize i18next once
 * Using a singleton pattern to ensure i18next is only initialized once
 */
// Only initialize if not already initialized
if (!i18n.isInitialized) {
  i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      fallbackLng: 'en',
      debug: false, // Disable debug logs in all environments
      
      interpolation: {
        escapeValue: false // React already escapes values
      },
      
      react: {
        useSuspense: true
      },
      
      // Add keys immediately to prevent missing key warnings
      saveMissing: true,
      missingKeyHandler: (lng, ns, key, fallbackValue) => {
        // Silently add key as value for all languages to ensure no missing keys
        if (Array.isArray(lng)) {
          lng.forEach(language => {
            i18n.addResource(language, ns, key, key);
          });
        } else if (typeof lng === 'string') {
          i18n.addResource(lng, ns, key, key);
        }
      }
    });
} else {
  // Reuse existing i18next instance
}

/**
 * Activates a locale by setting language and document attributes
 * @param locale The locale to activate (e.g. 'en' or 'ar')
 */
export async function dynamicActivate(locale: LocaleType) {
  try {
    // Change language
    await i18n.changeLanguage(locale);
    
    // Set language attribute
    document.documentElement.lang = locale;
    
    // Set text direction (RTL for Arabic, LTR for others)
    document.documentElement.dir = RTL_LOCALES.includes(locale) ? 'rtl' : 'ltr';
    
    // Try to save the selected language to localStorage but handle errors silently
    try {
      localStorage.setItem('locale', locale);
    } catch (storageError) {
      // Continue despite localStorage error - settings won't persist across sessions
    }
    
    // Force a redraw by triggering a small change to document body
    const body = document.body;
    if (body) {
      const originalDisplay = body.style.display;
      body.style.display = 'none';
      // Force layout recalculation
      void body.offsetHeight;
      body.style.display = originalDisplay;
    }
    
    // Artificially delay to ensure DOM updates
    await new Promise(resolve => setTimeout(resolve, 10));
    
    return true;
  } catch (error) {
    // Silently fallback to English if activation fails
    if (locale !== 'en') {
      try {
        // Just set language without localStorage
        document.documentElement.lang = 'en';
        document.documentElement.dir = 'ltr';
        await i18n.changeLanguage('en');
      } catch (fallbackError) {
        // Continue with default browser behavior
      }
    }
    
    return false;
  }
}

// Initialize with current language
const initializeLocale = () => {
  // Default to English
  let currentLocale: LocaleType = 'en'; 
  
  try {
    // First, try to get language from URL path (highest priority)
    if (typeof window !== 'undefined') {
      const pathSegments = window.location.pathname.split('/').filter(Boolean);
      const pathLang = pathSegments[0];
      
      if (pathLang && isValidLocale(pathLang)) {
        // URL path language has highest priority
        currentLocale = pathLang;
        
        // Force update localStorage to match URL path for consistency
        try {
          localStorage.setItem('locale', pathLang);
        } catch (e) {
          // Ignore storage errors
        }
      } else {
        // If no path language, then try localStorage
        try {
          const storedLocale = localStorage.getItem('locale');
          if (storedLocale && isValidLocale(storedLocale)) {
            currentLocale = storedLocale as LocaleType;
          }
        } catch (error) {
          // Ignore localStorage errors
        }
      }
    }
  } catch (error) {
    // Silently fail - will use default locale
  }
  
  // Initialize with detected language
  try {
    i18n.changeLanguage(currentLocale);
    document.documentElement.lang = currentLocale;
    document.documentElement.dir = RTL_LOCALES.includes(currentLocale) ? 'rtl' : 'ltr';
    // No logging in production
  } catch (error) {
    // Last resort - set to English without i18next
    document.documentElement.lang = 'en';
    document.documentElement.dir = 'ltr';
  }
};

// Helper to validate locale strings
function isValidLocale(locale: string): locale is LocaleType {
  return ['en', 'ar', 'zh', 'ja', 'ru', 'de', 'fr', 'es', 'hi', 'tr', 'sv'].includes(locale);
};

// Initialize once when the module is loaded
initializeLocale();

export default i18n;